/**
 * Created by Jerker Nord on 2020-05-26.
 */
import {IScreenViewConfig, ScreenView} from "@nolimitcity/slot-game/bin/core/screen/ScreenView";
import {Translation} from "@nolimitcity/slot-game/bin/core/translation/Translation";
import {IResizeData} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {ParsedServerData} from "@nolimitcity/slot-game/bin/core/server/data/ParsedServerData";
import {Linear, TimelineLite, TweenLite} from "gsap";
import {MonkeysGoldFreeSpinIntroController} from "./MonkeysGoldFreeSpinIntroController";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {SpineTween} from "@nolimitcity/slot-game/bin/core/animation/SpineTween";
import {INolimitAudioPlayer} from "@nolimitcity/slot-audio/bin/INolimitAudioPlayer";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {MonkeysGoldSoundConfig} from "../../MonkeysGoldSoundConfig";
import {BitmapTextStyleData, MonkeysGoldBitmapText} from "../../symbols/MonkeysGoldBitmapText";
import {BitmapTextStyleOptions} from "@nolimitcity/slot-game/bin/core/text/BitmapTextStyleOptions";

export class MonkeysGoldFreeSpinIntroView extends ScreenView {

    protected readonly DEFAULT_MOBILE_SCALE:number = 1;
    protected readonly DEFAULT_PORTRAIT_SCALE:number = 1;
    private static GAME_INFO_TEXT_MAX_HEIGHT:number = 220;

    private _container:PIXI.Container;
    private _youWonText:PIXI.Text;
    private _blackGfx:PIXI.Graphics;
    private _darkenBackground:PIXI.Graphics;
    private _diceImage:PIXI.Sprite;
    private _freeSpinsImage:PIXI.Sprite;
    private _fsWon:number;
    private _fsWonText:PIXI.BitmapText;
    private _fsWonTextStyle:BitmapTextStyleData = {
        fontName : "NumbersWinCountUpCondensed",
        fontSize : 28
    };

    private _style = new PIXI.TextStyle({
        fill : 0xfefefe,
        fontFamily : "Open Sans",
        wordWrap : true,
        padding : 20,
        wordWrapWidth : 440,
        fontSize : 26,
        breakWords : true
    });


    private _spineIntro:PIXI.spine.Spine;

    constructor(config:IScreenViewConfig, autoClose:boolean = true) {
        super(config, autoClose);
    }

    protected initAnimations():void {
        super.initAnimations();

        this._blackGfx = new PIXI.Graphics();
        this._blackGfx.alpha = 0;
        this.addChild(this._blackGfx);
    }

    protected setButtonPosition(data:IResizeData):void {
        super.setButtonPosition(data);
        this._button.position.y = ((data.height - 720) / 2) + 596;
    }

    protected createGameGraphics():PIXI.DisplayObject {
        const cont:PIXI.Container = new PIXI.Container();

        this._darkenBackground = new PIXI.Graphics();
        this._darkenBackground.alpha = 0.5;

        this._youWonText = new PIXI.Text(Translation.translate("Roll the dice to get the value of the incremental Vine Multiplier. Fill an entire row and the multiplier will increase and stay for the remainder of the bonus feature."), this._style);
        this._youWonText.position.set(-288, -70);
        this._youWonText.alpha = 0;

        if(this._youWonText.height > MonkeysGoldFreeSpinIntroView.GAME_INFO_TEXT_MAX_HEIGHT) {
            let fontSize:number = 26;
            while(this._youWonText.height > MonkeysGoldFreeSpinIntroView.GAME_INFO_TEXT_MAX_HEIGHT) {
                fontSize--;
                this._youWonText.style.fontSize = fontSize;
            }
        }

        this._diceImage = new PIXI.Sprite(GameResources.getTextures("introDice")[0]);
        this._diceImage.position.set(170, -70);
        this._diceImage.alpha = 0;

        this._freeSpinsImage = new PIXI.Sprite(GameResources.getTextures("Intro/intro_new_separate")[0]);
        this._freeSpinsImage.position.set(-119, -160);
        this._freeSpinsImage.alpha = 0;

        this._spineIntro = new PIXI.spine.Spine(GameResources.getSpineAsset("spineBonusGameIntro"));
        this._spineIntro.position.set(0, 0);
        this._spineIntro.visible = false;

        this._fsWonText = new PIXI.BitmapText("", this._fsWonTextStyle);
        this._fsWonText.anchor = 0.5;
        this._fsWonText.position.set(-58, -135);
        this._fsWonText.alpha = 0;

        cont.addChild(this._darkenBackground);
        cont.addChild(this._spineIntro);
        cont.addChild(this._youWonText);
        cont.addChild(this._diceImage);
        cont.addChild(this._freeSpinsImage);
        cont.addChild(this._fsWonText);

        this._container = cont;

        return this._container;
    }

    protected onResize(resizeData:IResizeData):void {
        super.onResize(resizeData);
        this.position.x = 0 - (Math.ceil(resizeData.width / this._layer.scale.x) - 720) / 2;
        this.position.y = 0 - (Math.ceil(resizeData.height / this._layer.scale.y) - 720) / 2;
    }

    protected onResizeGameGraphics(resizeData:IResizeData):void {
        this._container.position.set(this._screenWidth * 0.5, this._screenHeight * 0.5);

        this._blackGfx.clear();
        this._blackGfx.beginFill(0x000000);
        this._blackGfx.drawRect(0, 0, this._screenWidth, this._screenHeight);
        this._blackGfx.endFill();

        this._darkenBackground.clear();
        this._darkenBackground.beginFill(0x000000);
        this._darkenBackground.drawRect(0, 0, this._screenWidth, this._screenHeight);
        this._darkenBackground.endFill();
        this._darkenBackground.position.set(-this._darkenBackground.width * 0.5, -this._darkenBackground.height * 0.5);
    }

    protected startPresentation(data:ParsedServerData, onShowComplete?:() => void):void {
        this._container.alpha = 0;
        this._button.alpha = 0;
        if(this._autoCloseTimer) {
            this._autoCloseTimer.alpha = 0;
        }

        this.alpha = 1;

        const tl:TimelineLite = new TimelineLite();
        tl.add(() => this.playSound());
        tl.add(this.fadeInBlack());
        tl.add(() => MonkeysGoldFreeSpinIntroController.actuallyDoTransition());
        tl.add(() => {
            this._container.alpha = 1;
            this._button.alpha = 1;
        });
        tl.add(() => this._blackGfx.alpha = 0);
        tl.add(() => {
            super.startPresentation(data, onShowComplete);
            if(this._autoCloseTimer) {
                this._autoCloseTimer.alpha = 1;
            }
        });
        tl.add(() => this._spineIntro.updateTransform());
        tl.add(() => this._spineIntro.visible = true);
        tl.add(() => this._spineIntro.skeleton.setSlotsToSetupPose());
        tl.add(new SpineTween(this._spineIntro, 0, "landscape_animation", 0));
        tl.add([
            new TweenLite(this._youWonText, 0.2, {alpha : 1, ease : Linear.easeNone}),
            new TweenLite(this._diceImage, 0.2, {alpha : 1, ease : Linear.easeNone}),
            new TweenLite(this._fsWonText, 0.2, {alpha : 1, ease : Linear.easeNone}),
            new TweenLite(this._freeSpinsImage, 0.2, {alpha : 1, ease : Linear.easeNone})
        ], "-=2.5");
        tl.add(() => this._spineIntro.state.setAnimation(0, "Loop_animation", true));
    }


    protected stopPresentation(closeCallback?:() => void):void {
        const timeline:TimelineLite = new TimelineLite();
        timeline.add(new TweenLite(this, 1, {alpha : 0, ease : Linear.easeNone}));
        timeline.add(() => this._spineIntro.visible = false);
        timeline.add(() => super.stopPresentation(closeCallback));
    }

    private fadeInBlack():TimelineLite {
        const tl:TimelineLite = new TimelineLite();
        tl.add(new TweenLite(this._blackGfx, 1, {alpha : 1, ease : Linear.easeNone}));
        return tl;

    }

    private fadeOutBlack():TimelineLite {
        const tl:TimelineLite = new TimelineLite();
        tl.add(new TweenLite(this._blackGfx, 1, {alpha : 0, ease : Linear.easeNone}));
        return tl;
    }

    private playSound() {
        (<INolimitAudioPlayer>SlotGame.sound).playTrack({name : (MonkeysGoldSoundConfig.instance.FS_INTRO)})
    }

    set fsWon(value:number) {
        this._fsWon = value;
        this._fsWonText.text = this._fsWon.toString();
    }
}
